import { forwardRef, useState } from 'react';
import cn from 'classnames';

import { getImage } from '@/common/lib/images';
import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import { type DiceGameCardProps } from '@/common/types/dice-game';
import { ClaimCashCardAnimation } from '@/components/Animations/ClaimItemAnimation/ClaimCashCardAnimation/ClaimCashCardAnimation';
import { StringMain } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { ActiveBorder } from '../ActiveBorder/ActiveBorder';

import css from './styles.module.scss';

import greenBgPng from '/img/dice-game/green-card-bg.png';
import greenBgWebp from '/img/dice-game/green-card-bg.webp';
import cashPng from '/img/icons/cash-large.png';
import cashWebp from '/img/icons/cash-large.webp';

const GameItemCard = forwardRef<HTMLDivElement, DiceGameCardProps>(
  ({ value, item, isActive, ...props }, ref) => {
    const cashFinalPosition = useAppSelector(
      (state) => state.diceGame.position.rewards.cash,
    );
    const stockFinalPosition = useAppSelector(
      (state) => state.diceGame.position.rewards[item!],
    );
    const skipAnimation = useAppSelector(
      (state) => state.diceGame.skipAnimation,
    );
    const [animationFinished, setAnimationFinished] = useState(false);

    if (value === undefined || !item) {
      return null;
    }

    const icon =
      item === 'cash'
        ? { img: cashPng, webp: cashWebp }
        : getImage({ name: item, field: 'icon' });

    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          css.gameItemCard,
          props.className,
          item === 'cash' && 'gap-[5px]',
        )}
      >
        <div className="absolute overflow-hidden rounded-xl top-0 left-0 w-full h-full">
          <Picture
            className="w-full h-full"
            img={greenBgPng}
            webp={greenBgWebp}
            alt="light"
          />
        </div>
        <div
          className={cn(
            'relative z-[1] ',
            item === 'cash' ? 'w-[50.97%] h-[50.97%]' : 'w-[77.87%] h-[77.87%]',
          )}
        >
          {!animationFinished && stockFinalPosition && !skipAnimation && (
            <ClaimCashCardAnimation
              icon={{ img: icon?.img, webp: icon?.webp }}
              reward={item}
              finalPositon={
                item === 'cash'
                  ? cashFinalPosition
                  : stockFinalPosition || undefined
              }
              type="arc"
              onAnimationEnd={() => setAnimationFinished(true)}
            />
          )}
          <Picture
            className="w-full h-full"
            img={icon?.img}
            webp={icon?.webp}
            alt="cash"
          />
        </div>
        <StringMain
          weight="bold"
          className={cn('relatie z-[1]', item !== 'cash' && '-translate-y-1/2')}
        >
          {toShortNumFormat(value)}
        </StringMain>
        {isActive && <ActiveBorder />}
      </div>
    );
  },
);

export { GameItemCard };
